import { useMemo, useState } from "react";
import {
  buildBreedingEggResultOperation,
  buildBreedingFreeSwimOperation,
  buildBreedingSpawnOperation,
  buildBreedingStageOperation,
} from "../operations/breedingInventoryOperations";
import type { HallPatrolAction, HallPatrolTankSnapshot } from "../qr/hallPatrol";
import { getOrCreateDeviceId } from "../sync/deviceIdentity";
import { persistAndSyncOperation } from "../sync/operationRuntime";
import type { OfflineOperation, OutboxSummary } from "../sync/types";
import { supabase } from "../auth/supabaseClient";
import { ConfirmationSheet } from "./ConfirmationSheet";
import { speakPersian } from "./audioGuidance";

interface BreedingActionPanelProps {
  farmId: string;
  userId: string;
  tank: HallPatrolTankSnapshot;
  action: HallPatrolAction;
  onSyncSummary?: (summary: OutboxSummary) => void;
  onCompleted?: () => void;
}

type QuantityPrecision = "unknown" | "estimated" | "exact";
type EggResult = "hatched" | "partial_hatch" | "eaten" | "white";

function actionLabel(action: HallPatrolAction): string {
  const labels: Partial<Record<HallPatrolAction, string>> = {
    spawn_record: "ثبت تخم‌ریزی جدید",
    egg_result_record: "ثبت نتیجه تخم",
    free_swim_record: "ثبت شنای آزاد",
    parent_feeding_record: "ثبت تغذیه از والدین",
    parent_separation_record: "ثبت جداسازی والدین",
    growout_record: "انتقال چرخه به گله رشد",
  };
  return labels[action] ?? action;
}

function eggResultLabel(value: EggResult): string {
  if (value === "hatched") return "هچ شده";
  if (value === "partial_hatch") return "هچ بخشی";
  if (value === "eaten") return "خورده شده";
  return "سفید کرده";
}

const EGG_RESULT_META: Record<EggResult, { label: string; icon: string }> = {
  hatched: { label: "هچ شده", icon: "🐣" },
  partial_hatch: { label: "هچ بخشی", icon: "🥚" },
  eaten: { label: "خورده شده", icon: "🍽️" },
  white: { label: "سفید کرده", icon: "⚪" },
};

function syncMessage(status: string): string {
  if (status === "synced") return "رویداد تکثیر ثبت و همگام شد.";
  if (status === "pending") return "رویداد آفلاین ثبت شد و در صف ارسال قرار گرفت.";
  if (status === "retryable_error") return "ثبت محلی حفظ شد؛ ارسال دوباره انجام می‌شود.";
  if (status === "terminal_conflict") return "ثبت محلی حفظ شد اما نیاز به بررسی تعارض دارد.";
  return "رویداد ثبت شد.";
}

export function BreedingActionPanel({
  farmId,
  userId,
  tank,
  action,
  onSyncSummary,
  onCompleted,
}: BreedingActionPanelProps) {
  const [eggResult, setEggResult] = useState<EggResult>("hatched");
  const [hatchPercent, setHatchPercent] = useState("");
  const [quantityPrecision, setQuantityPrecision] = useState<QuantityPrecision>("unknown");
  const [fryQuantity, setFryQuantity] = useState("");
  const [pending, setPending] = useState<OfflineOperation>();
  const [confirmationParts, setConfirmationParts] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string>();
  const [error, setError] = useState<string>();

  const breeding = tank.breeding;
  const cycleRequired = action !== "spawn_record";
  const canBuild = Boolean(breeding && (!cycleRequired || breeding.cycleId));

  const successfulEggResult = eggResult === "hatched" || eggResult === "partial_hatch";
  const quantityHint = useMemo(() => {
    if (!successfulEggResult) return "";
    if (quantityPrecision === "unknown") return "تعداد نامشخص ثبت می‌شود و عدد ساختگی ساخته نمی‌شود.";
    return quantityPrecision === "estimated"
      ? "تعداد به‌صورت تخمینی ثبت می‌شود."
      : "تعداد دقیق ثبت می‌شود.";
  }, [quantityPrecision, successfulEggResult]);

  function requestConfirmation() {
    setMessage(undefined);
    setError(undefined);

    if (!breeding || (cycleRequired && !breeding.cycleId)) {
      setError("اطلاعات canonical جفت یا چرخه برای این عملیات موجود نیست.");
      return;
    }

    const context = {
      actorId: userId,
      farmId,
      deviceId: getOrCreateDeviceId(),
      performedAt: new Date().toISOString(),
    };

    try {
      if (action === "spawn_record") {
        const operation = buildBreedingSpawnOperation(context, {
          pairId: breeding.pairId,
          pairVersion: breeding.pairVersion,
          tankId: tank.tankId,
        });
        setPending(operation);
        setConfirmationParts([
          `تانک ${tank.tankCode}`,
          "ثبت یک Spawn جدید با Cycle UUID مستقل",
          "چرخه قبلی حذف یا بازنویسی نمی‌شود",
        ]);
        return;
      }

      if (!breeding.cycleId || breeding.cycleVersion === undefined) {
        setError("نسخه canonical چرخه در snapshot موجود نیست.");
        return;
      }

      if (action === "egg_result_record") {
        const parsedHatch = hatchPercent.trim() === "" ? undefined : Number(hatchPercent);
        const parsedQuantity =
          quantityPrecision === "unknown" || fryQuantity.trim() === ""
            ? undefined
            : Number(fryQuantity);

        const operation = buildBreedingEggResultOperation(context, {
          cycleId: breeding.cycleId,
          cycleVersion: breeding.cycleVersion,
          eggResult,
          ...(parsedHatch === undefined ? {} : { hatchPercent: parsedHatch }),
          quantityPrecision,
          ...(parsedQuantity === undefined ? {} : { fryQuantity: parsedQuantity }),
        });

        setPending(operation);
        setConfirmationParts([
          `تانک ${tank.tankCode}`,
          `نتیجه: ${eggResultLabel(eggResult)}`,
          successfulEggResult
            ? quantityPrecision === "unknown"
              ? "تعداد بچه: نامشخص"
              : `تعداد بچه: ${parsedQuantity}`
            : "برای این نتیجه گله جدید ساخته نمی‌شود",
        ]);
        return;
      }

      if (action === "free_swim_record") {
        setPending(
          buildBreedingFreeSwimOperation(context, {
            cycleId: breeding.cycleId,
            cycleVersion: breeding.cycleVersion,
          }),
        );
        setConfirmationParts([`تانک ${tank.tankCode}`, "شنای آزاد"]);
        return;
      }

      const stage =
        action === "parent_feeding_record"
          ? "parent_feeding"
          : action === "parent_separation_record"
            ? "separated"
            : action === "growout_record"
              ? "growout"
              : undefined;

      if (!stage) {
        setError("این اکشن در M3 lifecycle پشتیبانی نمی‌شود.");
        return;
      }

      setPending(
        buildBreedingStageOperation(context, {
          cycleId: breeding.cycleId,
          cycleVersion: breeding.cycleVersion,
          stage,
        }),
      );
      setConfirmationParts([
        `تانک ${tank.tankCode}`,
        actionLabel(action),
        ...(action === "parent_separation_record"
          ? ["جداسازی مستقل از state خطی چرخه ثبت می‌شود"]
          : []),
      ]);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "اطلاعات تکثیر معتبر نیست.");
    }
  }

  async function confirm() {
    if (!pending) return;

    try {
      setBusy(true);
      setError(undefined);
      const result = await persistAndSyncOperation(
        supabase,
        pending,
        typeof navigator === "undefined" ? true : navigator.onLine !== false,
      );
      onSyncSummary?.(result.summary);
      setMessage(syncMessage(result.operation.status));
      setPending(undefined);

      if (result.operation.status === "synced") {
        onCompleted?.();
      }
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "ثبت رویداد تکثیر انجام نشد.");
    } finally {
      setBusy(false);
    }
  }

  if (!canBuild) {
    return (
      <section className="section-card">
        <p className="settings-warning">
          این تانک جفت/چرخه canonical لازم برای این عملیات را ندارد.
        </p>
      </section>
    );
  }

  return (
    <section className="section-card" aria-labelledby="breeding-action-title">
      <div className="section-title-row">
        <div>
          <p className="section-kicker">تکثیر</p>
          <h2 id="breeding-action-title">{actionLabel(action)}</h2>
        </div>
        <span className="muted-chip">تانک {tank.tankCode}</span>
      </div>

      <button
        type="button"
        className="secondary-button large-button"
        onClick={() =>
          speakPersian(
            `${actionLabel(action)} برای تانک ${tank.tankCode}. گزینه مناسب را انتخاب کنید.`,
          )
        }
      >
        🔊 خواندن راهنمای این مرحله
      </button>

      {action === "spawn_record" ? (
        <p className="section-note">
          Spawn جدید با شناسه چرخه مستقل ثبت می‌شود؛ وجود بچه‌های cycle قبلی مانع ثبت چرخه جدید نیست.
        </p>
      ) : null}

      {action === "egg_result_record" ? (
        <>
          <div className="operation-field-grid">
            <div>
              <p className="section-kicker">نتیجه تخم</p>
              <div className="quick-grid">
                {(Object.keys(EGG_RESULT_META) as EggResult[]).map((value) => (
                  <button
                    type="button"
                    className={eggResult === value ? "quick-button active" : "quick-button"}
                    key={value}
                    aria-label={EGG_RESULT_META[value].label}
                    onClick={() => {
                      setEggResult(value);
                      speakPersian(`نتیجه تخم: ${EGG_RESULT_META[value].label}`);
                    }}
                  >
                    <span aria-hidden="true">{EGG_RESULT_META[value].icon}</span>
                    <strong>{EGG_RESULT_META[value].label}</strong>
                  </button>
                ))}
              </div>
            </div>
            <label>
              درصد هچ
              <input
                type="number"
                min="0"
                max="100"
                step="1"
                value={hatchPercent}
                onChange={(event) => setHatchPercent(event.target.value)}
              />
            </label>
            {successfulEggResult ? (
              <>
                <label>
                  دقت تعداد
                  <select
                    value={quantityPrecision}
                    onChange={(event) =>
                      setQuantityPrecision(event.target.value as QuantityPrecision)
                    }
                  >
                    <option value="unknown">نامشخص</option>
                    <option value="estimated">تخمینی</option>
                    <option value="exact">دقیق</option>
                  </select>
                </label>
                {quantityPrecision !== "unknown" ? (
                  <label>
                    تعداد بچه
                    <input
                      type="number"
                      min="0"
                      step="1"
                      value={fryQuantity}
                      onChange={(event) => setFryQuantity(event.target.value)}
                    />
                  </label>
                ) : null}
              </>
            ) : null}
          </div>
          {quantityHint ? <p className="section-note">{quantityHint}</p> : null}
        </>
      ) : null}

      {action === "parent_separation_record" ? (
        <p className="section-note">
          این گزینه جداسازی والدین از fry را ثبت می‌کند؛ «جداسازی جفت» مدیریتی مسیر جداگانه دارد.
        </p>
      ) : null}

      {action === "growout_record" ? (
        <p className="section-note">
          Growout فقط پس از ثبت جداسازی والدین و قرار گرفتن گله در تانک رشد پذیرفته می‌شود.
        </p>
      ) : null}

      <button
        type="button"
        className="primary-button large-button"
        disabled={busy}
        onClick={requestConfirmation}
      >
        {busy ? "در حال ثبت…" : "بررسی و تأیید"}
      </button>

      {message ? <p className="operation-success">{message}</p> : null}
      {error ? <p className="settings-warning">{error}</p> : null}

      {pending ? (
        <ConfirmationSheet
          title="تأیید رویداد تکثیر"
          parts={confirmationParts}
          speechEnabled
          fastConfirmEnabled
          onCancel={() => setPending(undefined)}
          onConfirm={() => void confirm()}
        />
      ) : null}
    </section>
  );
}
